import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspacePlanStageEventType,
  CaraWorkspaceTool
} from '@oysterjs/ais/api/graphql';

import {
  applicationFileGeneratingLabel,
  docxEditorToolTitle,
  isDocxApplicationFile
} from './utils';
import { searchIntegrationTitle } from './search-integration';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

/** AMS integration write tools and the display name shown for each. */
const AMS_TOOL_NAMES: Partial<Record<CaraWorkspaceTool, string>> = {
  [CaraWorkspaceTool.AgencyzoomWrite]: 'AgencyZoom',
  [CaraWorkspaceTool.HawksoftWrite]: 'HawkSoft',
  [CaraWorkspaceTool.NowcertsWrite]: 'NowCerts',
  [CaraWorkspaceTool.EzlynxWrite]: 'EZLynx',
  [CaraWorkspaceTool.Ams360Write]: 'AMS360',
  [CaraWorkspaceTool.QqcatalystWrite]: 'QQCatalyst'
};

/**
 * Returns the display title for a stage event, using the same strings as the
 * conversation event renderers. Returns `null` for events that are not rendered
 * (e.g. FileEditor READ operations, suppressed errors).
 *
 * Keep this in sync with `conversation-stage-event-renderers.tsx`.
 */
export const getStepTitle = (event: StageEvent): string | null => {
  if (event.type === CaraWorkspacePlanStageEventType.Thought) {
    return event.title || 'Thinking...';
  }

  switch (event.toolCall?.type) {
    case CaraWorkspaceTool.SearchFile:
      return 'Searching files';

    case CaraWorkspaceTool.SearchKnowledgeBase:
      return 'Searching knowledge base';

    case CaraWorkspaceTool.SearchKnowledgeGraph:
      return 'Searching knowledge graph';

    case CaraWorkspaceTool.SearchIntegration:
      return searchIntegrationTitle(event.toolCall.searchIntegrationTool);

    case CaraWorkspaceTool.SearchWeb:
      return 'Searching the web';

    case CaraWorkspaceTool.WriteIntegration:
      return 'Pushing data via integration';

    case CaraWorkspaceTool.DraftEmail: {
      const op = event.toolCall?.draftEmailTool?.input?.operation;
      if (op === 'UPDATE_DRAFT') return 'Updating email...';
      if (op && op !== 'CREATE_DRAFT' && op !== 'CREATE_FROM_TEMPLATE') return null;
      return 'Drafting email...';
    }

    case CaraWorkspaceTool.DraftOutboundCall:
      return 'Drafting outbound call...';

    case CaraWorkspaceTool.DraftDocument:
      return 'Drafting document...';

    case CaraWorkspaceTool.FillTemplate:
      return 'Filling template...';

    case CaraWorkspaceTool.FileEditor: {
      if (event.error || event.toolCall?.fileEditorTool?.input?.operation === 'READ') return null;
      return event.toolCall?.fileEditorTool?.input?.title || null;
    }

    case CaraWorkspaceTool.PdfEditor: {
      const op = event.toolCall?.pdfEditorTool?.input?.operation;
      if (op === 'LIST') return 'Listing available custom forms';
      if (op === 'DESCRIBE' || op === 'INSPECT_FORM_FIELDS') return 'Inspecting PDF form';
      if (op === 'FILL' || op === 'FILL_FORM_FIELDS') return 'Filling PDF form';
      return null;
    }

    case CaraWorkspaceTool.ApplicationFileGenerator: {
      const appFileTool = event.toolCall?.applicationFileGeneratorTool;
      return (
        'Drafting ' +
        applicationFileGeneratingLabel(
          isDocxApplicationFile({ toolCall: appFileTool ?? undefined }),
          true
        )
      );
    }

    case CaraWorkspaceTool.DocxEditor:
      return docxEditorToolTitle(event.toolCall?.docxEditorTool) || null;

    case CaraWorkspaceTool.XlsxEditor:
      return event.toolCall?.xlsxEditorTool?.input?.title?.trim() || null;

    case CaraWorkspaceTool.CodeExecution:
      return event.toolCall?.codeExecutionTool?.input?.title?.trim() || null;

    default: {
      const toolType = event.toolCall?.type;
      const amsName = toolType ? AMS_TOOL_NAMES[toolType] : undefined;
      return amsName ? `Updating ${amsName}` : null;
    }
  }
};
